import React from 'react';
import ReactDOM from 'react-dom/client';
import { BoltIcon, ChartBarIcon, GlobeAltIcon, LightBulbIcon, CurrencyDollarIcon, ClockIcon } from '@heroicons/react/24/outline';

function QuickLookupResult({ 
  keywordData, 
  analysisSource, 
  quickLookupPath, 
  allKeywordsPath 
}) {
  const formatNumber = (num) => {
    if (!num || num === 'N/A') return 'N/A';
    return parseInt(num).toLocaleString();
  };

  const formatCurrency = (amount) => {
    if (!amount || amount === 'N/A') return 'N/A';
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const getDifficultyColor = (kd) => {
    if (!kd || kd === 'N/A') return 'text-gray-400';
    const score = parseInt(kd);
    if (score <= 30) return 'text-green-400';
    if (score <= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getDifficultyLabel = (kd) => {
    if (!kd || kd === 'N/A') return 'Unknown';
    const score = parseInt(kd);
    if (score <= 30) return 'Easy';
    if (score <= 60) return 'Medium';
    return 'Hard';
  };

  const getDifficultyBgColor = (kd) => {
    if (!kd || kd === 'N/A') return 'bg-gray-500/20';
    const score = parseInt(kd);
    if (score <= 30) return 'bg-green-500/20';
    if (score <= 60) return 'bg-yellow-500/20';
    return 'bg-red-500/20';
  };

  const getBuildabilityColor = (buildable) => {
    if (buildable === true) return 'text-green-400';
    if (buildable === false) return 'text-red-400';
    return 'text-gray-400';
  };

  const getBuildabilityLabel = (buildable) => {
    if (buildable === true) return 'Yes';
    if (buildable === false) return 'No';
    return 'Unknown';
  };

  const getEffortColor = (effort) => {
    if (!effort || effort === 'N/A') return 'text-gray-400';
    const score = parseInt(effort);
    if (score === 1 || score === 2) return 'text-green-400';
    if (score === 3) return 'text-yellow-400';
    if (score === 4) return 'text-orange-400';
    if (score === 5) return 'text-red-400';
    return 'text-gray-400';
  };

  const getEffortLabel = (effort) => {
    if (!effort || effort === 'N/A') return 'Unknown';
    const score = parseInt(effort);
    if (score <= 2) return 'Low';
    if (score <= 4) return 'Medium';
    return 'High';
  };

  const toggleFavorite = async () => {
    try {
      const response = await fetch(`/keywords/${keywordData.id}/toggle_favorite`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // Reload the page to reflect the updated favorites status
          window.location.reload();
        } else {
          console.error('Failed to toggle favorite:', result.error);
        }
      } else {
        console.error('Failed to toggle favorite:', response.statusText);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const deleteKeyword = async () => {
    if (!confirm(`Are you sure you want to delete the keyword "${keywordData.keyword}"?`)) {
      return;
    }

    try {
      const response = await fetch(`/keywords/${keywordData.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // Redirect to keywords list after deletion
          window.location.href = '/keywords';
        } else {
          console.error('Failed to delete keyword:', result.error);
        }
      } else {
        console.error('Failed to delete keyword:', response.statusText);
      }
    } catch (error) {
      console.error('Error deleting keyword:', error);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex items-center justify-center space-x-2 mb-3">
          <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
            <BoltIcon className="w-4 h-4 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
              Keyword Analysis
            </h1>
          </div>
        </div>
        <div className="flex items-center justify-center space-x-3">
          <div className="bg-slate-700/30 rounded-lg px-4 py-2 border border-slate-600/50">
            <span className="text-lg font-semibold text-white">{keywordData.keyword}</span>
          </div>
          <button
            onClick={toggleFavorite}
            className={`p-2 rounded-lg transition-all duration-200 ${
              keywordData.favorited 
                ? 'text-pink-400 hover:text-pink-300 bg-pink-500/10 hover:bg-pink-500/20 border border-pink-500/30' 
                : 'text-gray-400 hover:text-pink-400 hover:bg-pink-500/10 border border-slate-600/50 hover:border-pink-500/30'
            }`}
            title={keywordData.favorited ? 'Remove from favorites' : 'Add to favorites'}
          >
            <svg className="w-5 h-5" fill={keywordData.favorited ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </button>
          <button
            onClick={deleteKeyword}
            className="p-2 rounded-lg transition-all duration-200 text-gray-400 hover:text-red-400 hover:bg-red-500/10 border border-slate-600/50 hover:border-red-500/30"
            title="Delete keyword"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Main metrics grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Volume Index */}
        <div className="bg-slate-800/40 backdrop-blur-sm rounded-lg border border-slate-700/50 p-4">
          <div className="flex items-center space-x-2 mb-2">
            <div className="w-7 h-7 bg-cyan-500/20 rounded-lg flex items-center justify-center">
              <ChartBarIcon className="w-4 h-4 text-cyan-400" />
            </div>
            <h3 className="font-medium text-white text-sm">Volume Index</h3>
          </div>
          <p className="text-xl font-bold text-cyan-400">{formatNumber(keywordData.volume)}</p>
          <p className="text-xs text-gray-400 mt-1">Monthly searches</p>
        </div>

        {/* Difficulty Rating */}
        <div className="bg-slate-800/40 backdrop-blur-sm rounded-lg border border-slate-700/50 p-4">
          <div className="flex items-center space-x-2 mb-2">
            <div className="w-7 h-7 bg-yellow-500/20 rounded-lg flex items-center justify-center">
              <GlobeAltIcon className="w-4 h-4 text-yellow-400" />
            </div>
            <h3 className="font-medium text-white text-sm">Difficulty Rating</h3>
          </div>
          <div className="flex items-center space-x-2">
            <p className={`text-xl font-bold ${getDifficultyColor(keywordData.kd)}`}>
              {keywordData.kd || 'N/A'}
            </p>
            <span className={`text-xs px-2 py-1 rounded-md text-white ${getDifficultyBgColor(keywordData.kd)}`}>
              {getDifficultyLabel(keywordData.kd)}
            </span>
          </div>
          <p className="text-xs text-gray-400 mt-1">Competition level</p>
        </div>

        {/* Effort Level x/5 */}
        <div className="bg-slate-800/40 backdrop-blur-sm rounded-lg border border-slate-700/50 p-4">
          <div className="flex items-center space-x-2 mb-2">
            <div className="w-7 h-7 bg-orange-500/20 rounded-lg flex items-center justify-center">
              <ClockIcon className="w-4 h-4 text-orange-400" />
            </div>
            <h3 className="font-medium text-white text-sm">Effort Level</h3>
          </div>
          <p className={`text-xl font-bold ${getEffortColor(keywordData.ai_effort)}`}>
            {keywordData.ai_effort ? `${keywordData.ai_effort}/5` : 'N/A'}
          </p>
          <p className="text-xs text-gray-400 mt-1">Development complexity</p>
        </div>

        {/* AI Buildable */}
        <div className="bg-slate-800/40 backdrop-blur-sm rounded-lg border border-slate-700/50 p-4">
          <div className="flex items-center space-x-2 mb-2">
            <div className="w-7 h-7 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <LightBulbIcon className="w-4 h-4 text-purple-400" />
            </div>
            <h3 className="font-medium text-white text-sm">AI Buildable</h3>
          </div>
          <p className={`text-xl font-bold ${getBuildabilityColor(keywordData.ai_buildable)}`}>
            {getBuildabilityLabel(keywordData.ai_buildable)}
          </p>
          <p className="text-xs text-gray-400 mt-1">Can be built with AI</p>
        </div>
      </div>

      {/* AI Analysis Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Project Details */}
        <div className="bg-slate-800/40 backdrop-blur-sm rounded-lg border border-slate-700/50 p-4">
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center space-x-2">
            <ClockIcon className="w-4 h-4 text-blue-400" />
            <span>Project Details</span>
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Classification</label>
              <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-lg text-sm border border-blue-500/30">
                {keywordData.ai_type || 'N/A'}
              </span>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Build Category</label>
              <span className="px-3 py-1 bg-indigo-500/20 text-indigo-300 rounded-lg text-sm border border-indigo-500/30">
                {keywordData.project_category || 'N/A'}
              </span>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Effort Level</label>
              <span className="px-3 py-1 bg-orange-500/20 text-orange-300 rounded-lg text-sm border border-orange-500/30">
                {getEffortLabel(keywordData.ai_effort)}
              </span>
            </div>
          </div>
        </div>

        {/* MVP & Monetization */}
        <div className="bg-slate-800/40 backdrop-blur-sm rounded-lg border border-slate-700/50 p-4">
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center space-x-2">
            <CurrencyDollarIcon className="w-4 h-4 text-green-400" />
            <span>Business Insights</span>
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">MVP Example</label>
              <p className="text-gray-300 bg-slate-700/30 rounded-lg p-3 border border-slate-600/30">
                {keywordData.ai_mvp || 'No example available'}
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Monetization Strategy</label>
              <p className="text-gray-300 bg-slate-700/30 rounded-lg p-3 border border-slate-600/30">
                {keywordData.ai_monetize || 'No strategy available'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* AI Notes */}
      {keywordData.ai_notes && keywordData.ai_notes !== 'AI analysis unavailable' && (
        <div className="bg-slate-800/40 backdrop-blur-sm rounded-lg border border-slate-700/50 p-4 mb-6">
          <h3 className="text-lg font-semibold text-white mb-3">AI Analysis Notes</h3>
          <p className="text-gray-300 leading-relaxed text-sm">{keywordData.ai_notes}</p>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <a
          href={quickLookupPath}
          className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-lg shadow-purple-500/25 text-center text-sm"
        >
          <span className="flex items-center justify-center space-x-2">
            <BoltIcon className="w-4 h-4" />
            <span>Analyze New Keyword</span>
          </span>
        </a>
        
        <a
          href={allKeywordsPath}
          className="bg-slate-700/50 hover:bg-slate-600/50 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 border border-slate-600/50 text-center text-sm"
        >
          <span className="flex items-center justify-center space-x-2">
            <ChartBarIcon className="w-4 h-4" />
            <span>View All Results</span>
          </span>
        </a>
      </div>
    </div>
  );
}

// Auto-mount component
let quickLookupResultRoot = null;

function mountQuickLookupResult() {
  const el = document.getElementById('quick-lookup-result-root');
  if (el) {
    const props = window.quickLookupResultProps || {};

    if (quickLookupResultRoot) {
      quickLookupResultRoot.render(<QuickLookupResult {...props} />);
    } else {
      quickLookupResultRoot = ReactDOM.createRoot(el);
      quickLookupResultRoot.render(<QuickLookupResult {...props} />);
    }
  }
}

function cleanupQuickLookupResult() {
  if (quickLookupResultRoot) {
    quickLookupResultRoot.unmount();
    quickLookupResultRoot = null;
  }
}

// Mount on initial load
mountQuickLookupResult();

// Mount on Turbo navigation
document.addEventListener('turbo:load', mountQuickLookupResult);
document.addEventListener('turbo:before-visit', cleanupQuickLookupResult);

export default QuickLookupResult;